import { Heading } from 'almanac-ui/Heading/Heading'
import { Modal } from 'almanac-ui/Modal'
import { CreateKnowledgeBaseSnippetPayload } from 'api/clients/KnowledgeBaseClient'
import { EmojiPicker } from 'components/AlmanacFileTitle/EmojiPicker'
import { Form, Formik, useFormikContext } from 'formik'
import { BaseEmoji, KnowledgeBaseSnippet, KnowledgeBaseSnippetKind } from 'types'
import * as yup from 'yup'
import Styles from 'screens/BrandKits/components/SnippetForm/SnippetForm.module.scss'
import { SnippetKindLayout } from 'screens/BrandKits/utils'
import { Trash } from 'almanac-ui/icons'
import { Select } from 'components/Select/Select'
import { Item } from '@react-stately/collections'
import { Checkbox } from 'components/Form/Checkbox/Checkbox'
import { capitalize } from 'lodash'
import { ArrowRight } from 'blaze-ui/icons'
import { Input } from 'almanac-ui/TextInputField/Input'

const MAX_CONTENT_LENGTH = 700

interface SnippetFormProps {
  snippet: CreateKnowledgeBaseSnippetPayload | KnowledgeBaseSnippet
  onSubmit: (payload: CreateKnowledgeBaseSnippetPayload) => Promise<void>
  onSecondaryAction?: () => void
  nameError?: string
  onResetNameError?: () => void
}

const SubmitButton = ({ submitForm, isUpdating }: { submitForm: () => Promise<void>; isUpdating: boolean }) => {
  const { setFieldError } = useFormikContext()

  return (
    <Modal.FooterButton
      data-testid="createSnippetButton"
      size="lg"
      endIcon={ArrowRight}
      onPress={() => submitForm().catch((err) => setFieldError('name', err.message))}
    >
      {isUpdating ? 'Update Snippet' : 'Create Snippet'}
    </Modal.FooterButton>
  )
}

export const SnippetForm = ({ snippet, onSubmit, onSecondaryAction }: SnippetFormProps) => {
  const schema = yup.object().shape({
    name: yup.string().required(),
    content: yup.string().required(),
  })

  const isUpdating = 'id' in snippet

  return (
    <Formik initialValues={snippet} validationSchema={schema} onSubmit={onSubmit}>
      {({ submitForm, values, touched, errors, setFieldValue }) => (
        <>
          <Modal.Content>
            <Form className={Styles.formContainer}>
              <div className={Styles.formGroup}>
                <Heading level={4}>
                  Name & Type <span className={Styles.star}>*</span>
                </Heading>
                <div className={Styles.nameContainer}>
                  <div className={Styles.emojiContainer}>
                    <EmojiPicker
                      emoji={values.emoji || ''}
                      buttonClass={Styles.emojiButton}
                      onSelect={(emoji: BaseEmoji | null) => setFieldValue('emoji', emoji?.native)}
                      size="lg"
                    />
                  </div>
                  <div className={Styles.inputContainer}>
                    <Input
                      value={values.name}
                      onChange={(e) => setFieldValue('name', e.currentTarget.value)}
                      size="lg"
                      name="name"
                      placeholder="What do you want to call this snippet?"
                      errorMessage={touched.name && capitalize(errors.name)}
                      isValid={touched.name && !errors.name}
                      autoComplete="off"
                    />
                  </div>
                  <Select
                    size="lg"
                    variant="secondary"
                    spanClass={Styles.selectSpan}
                    selectedKey={values.kind}
                    aria-labelledby="knowledge-base-snippet-type-label"
                    id="knowledge-base-snippet-type"
                    onSelectionChange={(kind) => setFieldValue('kind', String(kind))}
                  >
                    {Object.values(KnowledgeBaseSnippetKind).map((kind) => (
                      <Item key={kind}>{SnippetKindLayout[kind].title}</Item>
                    ))}
                  </Select>
                </div>
              </div>
              <div className={Styles.formGroup}>
                <Heading level={4}>{SnippetKindLayout[values.kind].title}</Heading>
                <Input
                  className={Styles.input}
                  value={values.content}
                  onChange={(e) => setFieldValue('content', e.currentTarget.value)}
                  size="xl"
                  name="content"
                  isTextarea
                  resizable={false}
                  rows={8}
                  placeholder={SnippetKindLayout[values.kind].description}
                  errorMessage={touched.content && errors.content && 'Please enter description'}
                  isValid={touched.content && !errors.content}
                  maxLength={MAX_CONTENT_LENGTH}
                />
                <div className={Styles.charCounter}>
                  {values.content.length}/{MAX_CONTENT_LENGTH}
                </div>
                <Checkbox
                  className={Styles.makeDefaultCheckbox}
                  isSelected={values.default}
                  onChange={(isSelected) => setFieldValue('default', isSelected)}
                  size="lg"
                >
                  Make default for {SnippetKindLayout[values.kind].title}
                </Checkbox>
              </div>
            </Form>
          </Modal.Content>
          <Modal.Footer>
            <Modal.FooterContent slot="left">
              <Modal.FooterButton
                onPress={onSecondaryAction}
                size="lg"
                variant="tertiary"
                frontIcon={isUpdating ? Trash : undefined}
              >
                {isUpdating ? 'Delete' : 'Back'}
              </Modal.FooterButton>
            </Modal.FooterContent>
            <Modal.FooterContent slot="right">
              <SubmitButton submitForm={submitForm} isUpdating={isUpdating} />
            </Modal.FooterContent>
          </Modal.Footer>
        </>
      )}
    </Formik>
  )
}
